import { getAdminClient } from "@/lib/supabase-admin";
import type { Tables, Enums } from "@/lib/supabase.types";

export type AgreementRow = Tables<"vipps_agreements">;
export type ChargeRow = Tables<"vipps_charges">;
export type AgreementStatus = Enums<"vipps_agreement_status">;
export type ChargeStatus = Enums<"vipps_charge_status">;

const NOK = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const NB_DT = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

export function formatNok(amount: number | null | undefined): string {
  return NOK.format(amount ?? 0);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return NB_DT.format(new Date(iso));
}

export const AGREEMENT_LABEL: Record<AgreementStatus, string> = {
  PENDING: "Venter",
  ACTIVE: "Aktiv",
  STOPPED: "Stoppet",
  EXPIRED: "Utløpt",
};

export const CHARGE_LABEL: Record<ChargeStatus, string> = {
  PENDING: "Venter",
  DUE: "Forfaller",
  RESERVED: "Reservert",
  CHARGED: "Trukket",
  FAILED: "Feilet",
  CANCELLED: "Avbrutt",
  PARTIALLY_REFUNDED: "Delvis refundert",
  REFUNDED: "Refundert",
  PROCESSING: "Behandles",
};

export interface DonorRow extends AgreementRow {
  total_charged: number;
  failed_count: number;
  last_charge_at: string | null;
}

export interface DonorOverview {
  donors: DonorRow[];
  active_count: number;
  monthly_sum: number;
  total_charged: number;
}

export async function fetchDonorOverview(): Promise<DonorOverview> {
  const supabase = getAdminClient();
  const [agrRes, chRes] = await Promise.all([
    supabase
      .from("vipps_agreements")
      .select("*")
      .order("created_at", { ascending: false }),
    supabase
      .from("vipps_charges")
      .select("agreement_id, amount_nok, status, charged_at"),
  ]);
  if (agrRes.error) throw agrRes.error;
  if (chRes.error) throw chRes.error;

  const stats = new Map<string, { total: number; failed: number; last: string | null }>();
  for (const c of chRes.data ?? []) {
    const slot = stats.get(c.agreement_id) ?? { total: 0, failed: 0, last: null };
    if (c.status === "CHARGED") {
      slot.total += c.amount_nok;
      if (c.charged_at && (!slot.last || c.charged_at > slot.last)) slot.last = c.charged_at;
    }
    if (c.status === "FAILED") slot.failed += 1;
    stats.set(c.agreement_id, slot);
  }

  const donors: DonorRow[] = (agrRes.data ?? []).map((a) => {
    const s = stats.get(a.id);
    return {
      ...a,
      total_charged: s?.total ?? 0,
      failed_count: s?.failed ?? 0,
      last_charge_at: s?.last ?? null,
    };
  });

  const active = donors.filter((d) => d.status === "ACTIVE");
  return {
    donors,
    active_count: active.length,
    monthly_sum: active.reduce((sum, d) => sum + d.amount_nok, 0),
    total_charged: donors.reduce((sum, d) => sum + d.total_charged, 0),
  };
}

export async function fetchAgreementCharges(agreementId: string): Promise<ChargeRow[]> {
  const { data, error } = await getAdminClient()
    .from("vipps_charges")
    .select("*")
    .eq("agreement_id", agreementId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}
